import { Tooltip, TooltipV2 } from "@/components/TooltipV2";
import { Button, ButtonProps } from "@/components/interactive/Button";
import {
  ButtonV2,
  ButtonV2Props,
  IconButtonV2,
  IconButtonV2Props,
  LinkButtonV2,
  LinkButtonV2Props,
} from "@/components/interactive/ButtonV2";
import { IconButton, IconButtonProps } from "@/components/interactive/IconButton";
import { PermissionDebugMessage } from "@/components/permission/PermissionDebugMessage";
import {
  IconButtonDropdownMenuItemV2,
  type IconButtonDropdownMenuItemV2Props,
} from "@/components/interactive/IconButtonDropdownMenuV2";
import {
  useGetPermissionWarningForEndpoint,
  useHasPermissionForEndpoint,
} from "@/utils/hooks/use-permissions";
import type { DescMethod } from "@bufbuild/protobuf";
import { Placement } from "@floating-ui/react-dom-interactions";
import React, { CSSProperties } from "react";

export interface PermissionRequiredButtonProps extends ButtonProps {
  actionName?: string;
  endpoint: DescMethod;
  placement?: Placement;
}

const tooltipStyle: CSSProperties = { maxWidth: "800px" };

function usePermissionRequiredAction(endpoint: DescMethod, actionName?: string) {
  const { hasPermission, loaded: permissionLoaded } = useHasPermissionForEndpoint(endpoint);
  const warningText = useGetPermissionWarningForEndpoint({ method: endpoint, actionName });

  return {
    hasPermission,
    permissionLoaded,
    showPermissionWarning: permissionLoaded && !hasPermission,
    rolesText: (
      <div>
        {warningText}
        <PermissionDebugMessage />
      </div>
    ),
  };
}

export const PermissionRequiredButton = React.forwardRef<
  HTMLButtonElement,
  PermissionRequiredButtonProps
>(function PermissionRequiredButton(
  { endpoint, disabled, loading, actionName, placement, ...rest },
  ref,
) {
  const { hasPermission, permissionLoaded, showPermissionWarning, rolesText } =
    usePermissionRequiredAction(endpoint, actionName);
  // button is always disabled until permissions are loaded, but we only display the permissions tooltip
  // if the permissions are fully loaded
  const button = (
    <Button
      ref={ref}
      {...rest}
      disabled={disabled || !hasPermission || !permissionLoaded}
      loading={loading || !permissionLoaded}
    />
  );

  if (showPermissionWarning) {
    return (
      <Tooltip content={rolesText} style={tooltipStyle} instant placement={placement}>
        <div>{button}</div>
      </Tooltip>
    );
  }
  return button;
});

export interface PermissionRequiredButtonV2Props extends ButtonV2Props {
  actionName?: string;
  endpoint: DescMethod;
  placement?: Placement;
}

export const PermissionRequiredButtonV2 = React.forwardRef<
  HTMLButtonElement,
  PermissionRequiredButtonV2Props
>(function PermissionRequiredButtonV2(
  { endpoint, disabled, loading, actionName, placement, ...rest },
  ref,
) {
  const { hasPermission, permissionLoaded, showPermissionWarning, rolesText } =
    usePermissionRequiredAction(endpoint, actionName);
  const button = (
    <ButtonV2
      ref={ref}
      {...rest}
      disabled={disabled || !hasPermission || !permissionLoaded}
      loading={loading || !permissionLoaded}
    />
  );

  if (showPermissionWarning) {
    return (
      <TooltipV2 content={rolesText} style={tooltipStyle} instant placement={placement}>
        <div>{button}</div>
      </TooltipV2>
    );
  }
  return button;
});

export interface PermissionRequiredLinkButtonV2Props extends LinkButtonV2Props {
  actionName?: string;
  endpoint: DescMethod;
  placement?: Placement;
}

export const PermissionRequiredLinkButtonV2 = React.forwardRef<
  HTMLAnchorElement,
  PermissionRequiredLinkButtonV2Props
>(function PermissionRequiredLinkButtonV2(
  { endpoint, disabled, loading, actionName, placement, ...rest },
  ref,
) {
  const { hasPermission, permissionLoaded, showPermissionWarning, rolesText } =
    usePermissionRequiredAction(endpoint, actionName);
  const link = (
    <LinkButtonV2
      ref={ref}
      {...rest}
      disabled={disabled || !hasPermission || !permissionLoaded}
      loading={loading || !permissionLoaded}
    />
  );

  if (showPermissionWarning) {
    return (
      <TooltipV2 content={rolesText} style={tooltipStyle} instant placement={placement}>
        <div>{link}</div>
      </TooltipV2>
    );
  }
  return link;
});

export interface PermissionRequiredDropdownMenuItemV2Props
  extends IconButtonDropdownMenuItemV2Props {
  actionName?: string;
  endpoint: DescMethod;
  placement?: Placement;
}

export const PermissionRequiredDropdownMenuItemV2: React.FC<
  PermissionRequiredDropdownMenuItemV2Props
> = ({ endpoint, disabled, actionName, placement, ...rest }) => {
  const { hasPermission, permissionLoaded, showPermissionWarning, rolesText } =
    usePermissionRequiredAction(endpoint, actionName);
  const menuItem = (
    <IconButtonDropdownMenuItemV2
      {...rest}
      disabled={disabled || !hasPermission || !permissionLoaded}
    />
  );

  if (showPermissionWarning) {
    return (
      <TooltipV2 content={rolesText} style={tooltipStyle} instant placement={placement}>
        <div className={"w-full"}>{menuItem}</div>
      </TooltipV2>
    );
  }

  return menuItem;
};

export interface PermissionRequiredIconButtonProps extends IconButtonProps {
  actionName?: string;
  endpoint: DescMethod;
  placement?: Placement;
}

export const PermissionRequiredIconButton = React.forwardRef<
  HTMLButtonElement,
  PermissionRequiredIconButtonProps
>(function PermissionRequiredIconButton(
  { endpoint, disabled, actionName, placement, ...rest },
  ref,
) {
  const { hasPermission, permissionLoaded, showPermissionWarning, rolesText } =
    usePermissionRequiredAction(endpoint, actionName);
  // button is always disabled until permissions are loaded, but we only display the permissions tooltip
  // if the permissions are fully loaded
  const button = (
    <IconButton ref={ref} disabled={disabled || !hasPermission || !permissionLoaded} {...rest} />
  );

  if (showPermissionWarning) {
    return (
      <Tooltip content={rolesText} style={tooltipStyle} instant placement={placement}>
        <div>{button}</div>
      </Tooltip>
    );
  }
  return button;
});

export interface PermissionRequiredIconButtonV2Props extends IconButtonV2Props {
  actionName?: string;
  endpoint: DescMethod;
  placement?: Placement;
}

export const PermissionRequiredIconButtonV2 = React.forwardRef<
  HTMLButtonElement,
  PermissionRequiredIconButtonV2Props
>(function PermissionRequiredIconButtonV2(
  { endpoint, disabled, loading, actionName, placement, ...rest },
  ref,
) {
  const { hasPermission, permissionLoaded, showPermissionWarning, rolesText } =
    usePermissionRequiredAction(endpoint, actionName);
  const button = (
    <IconButtonV2
      ref={ref}
      {...rest}
      disabled={disabled || !hasPermission || !permissionLoaded}
      loading={loading || !permissionLoaded}
    />
  );

  if (showPermissionWarning) {
    return (
      <TooltipV2 content={rolesText} style={tooltipStyle} instant placement={placement}>
        <div>{button}</div>
      </TooltipV2>
    );
  }

  return button;
});
